import { useCollaborateStore } from "../store/useCollaborateStore";
import type { CreateThreadInput, Thread, ThreadStatus } from "../types/collaborateThread";
import type { AnchorDescriptor } from "../types/collaborateThread";
import { normalizeCollaborateThread } from "../utils/normalizeCollaborateThread";
import { parseMentionIdsFromBody } from "../utils/parseMentions";
import { wrapMentionsInBody } from "../utils/wrapMentionsInBody";
import { isMockCollaborateApi, mockCollaborateApi } from "./mockApi";
import type { CollaborateApi } from "./types";

let collaborateApi: CollaborateApi = mockCollaborateApi;

export function setCollaborateApi(api: CollaborateApi): void {
  collaborateApi = api;
}

export function getCollaborateApi(): CollaborateApi {
  return collaborateApi;
}

/** Clear collaborate UI + thread cache (call on logout / before a new user session). */
export function resetCollaborateSession(): void {
  useCollaborateStore.getState().resetCollaborate();
}

function currentThreadQuery() {
  const { listView, versionFilter } = useCollaborateStore.getState();
  return { listView, versionFilter };
}

export async function hydrateCollaborateData(documentId?: string): Promise<void> {
  const api = getCollaborateApi();
  const store = useCollaborateStore.getState();
  store.setLoading(true);
  store.setThreadsPagination({ loadingInitial: true });
  try {
    const { listView, versionFilter } = store;
    const [users, current, summary, page] = await Promise.all([
      api.fetchUsers(),
      api.fetchCurrentUser(),
      api.fetchThreadSummary(documentId, { listView, versionFilter }),
      api.fetchThreadsPage({ listView, versionFilter }, documentId),
    ]);
    store.hydrate({
      users,
      currentUserId: current.id,
      threads: page.items.map(normalizeCollaborateThread),
      pagination: {
        cursor: page.nextCursor,
        hasMore: page.hasMore,
        total: page.total,
      },
      filterSummary: {
        listViewCounts: {
          comments: summary.listViewCounts.comments ?? 0,
          unread: summary.listViewCounts.unread ?? 0,
          mentions: summary.listViewCounts.mentions ?? 0,
          "all-approvals": summary.listViewCounts["all-approvals"] ?? 0,
          "pending-mine": summary.listViewCounts["pending-mine"] ?? 0,
          "requested-by-me": summary.listViewCounts["requested-by-me"] ?? 0,
        },
        versionCounts: summary.versionCounts ?? [],
      },
    });
  } catch (err) {
    store.resetCollaborate();
    throw err;
  } finally {
    store.setLoading(false);
    store.setThreadsPagination({ loadingInitial: false });
  }
}

/** @deprecated Use {@link hydrateCollaborateData}. */
export const hydrateCollaborateMockData = hydrateCollaborateData;

export async function refetchCollaborateThreads(documentId?: string): Promise<void> {
  const api = getCollaborateApi();
  const store = useCollaborateStore.getState();
  store.resetThreadPages();
  store.setLoading(true);
  try {
    const { listView, versionFilter } = store;
    const [summary, page] = await Promise.all([
      api.fetchThreadSummary(documentId, { listView, versionFilter }),
      api.fetchThreadsPage({ listView, versionFilter }, documentId),
    ]);
    store.setInitialThreadPage(
      {
        ...page,
        items: page.items.map(normalizeCollaborateThread),
      },
      summary,
    );
  } finally {
    store.setLoading(false);
  }
}

let loadMoreInFlight = false;

export async function loadMoreCollaborateThreads(documentId?: string): Promise<void> {
  const api = getCollaborateApi();
  const store = useCollaborateStore.getState();
  const { threadsPagination, listView, versionFilter } = store;
  if (
    loadMoreInFlight ||
    !store.hydrated ||
    !threadsPagination.hasMore ||
    threadsPagination.loadingMore ||
    threadsPagination.loadingInitial
  ) {
    return;
  }

  loadMoreInFlight = true;
  store.setThreadsPagination({ loadingMore: true });
  try {
    const page = await api.fetchThreadsPage(
      {
        listView,
        versionFilter,
        cursor: threadsPagination.cursor,
      },
      documentId,
    );
    store.appendThreadPage({
      ...page,
      items: page.items.map(normalizeCollaborateThread),
    });
  } catch {
    store.setThreadsPagination({ loadingMore: false });
  } finally {
    loadMoreInFlight = false;
  }
}

export async function syncCollaborateThreads(documentId?: string): Promise<void> {
  const api = getCollaborateApi();
  if (isMockCollaborateApi(api)) {
    return;
  }
  const store = useCollaborateStore.getState();
  if (!store.hydrated) {
    return;
  }
  try {
    const { listView, versionFilter } = currentThreadQuery();
    const [summary, page] = await Promise.all([
      api.fetchThreadSummary(documentId, { listView, versionFilter }),
      api.fetchThreadsPage({ listView, versionFilter }, documentId),
    ]);
    store.setFilterSummary(summary);
    store.mergeThreadUpdates(page.items.map(normalizeCollaborateThread));
    store.pruneReadThreadsFromUnreadList();
  } catch {
    // Keep the last synced state when background polling fails.
  }
}

export async function createThread(input: CreateThreadInput): Promise<Thread> {
  const api = getCollaborateApi();
  const thread = await api.createThread(input);
  if (!isMockCollaborateApi(api)) {
    useCollaborateStore.getState().applyThreadFromServer(normalizeCollaborateThread(thread));
  }
  return thread;
}

export async function addThreadReply(
  threadId: string,
  body: string,
  agreementVersionId?: string,
  versionTagged?: boolean,
): Promise<void> {
  const api = getCollaborateApi();
  const plain = body.trim();
  if (!plain) {
    return;
  }
  const users = Object.values(useCollaborateStore.getState().usersById);
  const mentionIds = parseMentionIdsFromBody(plain, users);
  const wrapped =
    plain.includes("<mention") || plain.includes("<signoff>")
      ? plain
      : wrapMentionsInBody(plain);

  if (isMockCollaborateApi(api)) {
    await api.addReply(threadId, wrapped);
    return;
  }

  const reply = await api.addReply(
    threadId,
    wrapped,
    mentionIds,
    agreementVersionId,
    versionTagged,
  );
  if (reply) {
    useCollaborateStore.getState().applyReplyFromServer(threadId, reply);
  }
}

export async function patchThreadStatus(
  threadId: string,
  status: ThreadStatus
): Promise<void> {
  if (status !== "approved" && status !== "rejected") {
    throw new Error("Only approved or rejected status can be patched.");
  }
  const api = getCollaborateApi();
  const updated = await api.patchThreadStatus(threadId, status);
  if (updated) {
    useCollaborateStore.getState().applyThreadFromServer(normalizeCollaborateThread(updated));
  }
}

export async function fetchPendingFocus(
  versionId: string,
): Promise<{ rootCommentId: string | null }> {
  const api = getCollaborateApi();
  if (isMockCollaborateApi(api)) {
    return { rootCommentId: null };
  }
  return api.fetchPendingFocus(versionId);
}

export async function patchThreadAnchor(
  threadId: string,
  anchor: AnchorDescriptor,
): Promise<Thread | null> {
  const api = getCollaborateApi();
  if (isMockCollaborateApi(api)) {
    return null;
  }
  const updated = await api.patchThreadAnchor(threadId, anchor);
  if (updated) {
    useCollaborateStore.getState().applyThreadFromServer(normalizeCollaborateThread(updated));
  }
  return updated;
}

export async function consumePendingFocus(
  versionId: string,
  currentVersionId: string,
): Promise<boolean> {
  const focus = await fetchPendingFocus(versionId);
  if (!focus.rootCommentId) {
    return false;
  }

  const store = useCollaborateStore.getState();
  if (!store.threadsById[focus.rootCommentId]) {
    const api = getCollaborateApi();
    if (!isMockCollaborateApi(api)) {
      try {
        const thread = await api.getThread(focus.rootCommentId);
        store.applyThreadFromServer(normalizeCollaborateThread(thread));
      } catch {
        return false;
      }
    }
  }

  await store.focusThread(focus.rootCommentId, currentVersionId);
  return true;
}

export async function fetchInboxSummary(contractId?: string) {
  const api = getCollaborateApi();
  return api.fetchInboxSummary(contractId);
}

export async function markThreadRead(threadId: string): Promise<void> {
  useCollaborateStore.getState().markThreadRead(threadId);
}

export { isMockCollaborateApi } from "./mockApi";
export type { CollaborateApi } from "./types";
